import { Injector, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MatProgressBar } from '@angular/material/progress-bar';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { SharedModule } from '@app/shared/shared.module';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { GlobalConfig, ToastrModule } from '@app/shared/features/toastr';
import * as WebFont from 'webfontloader';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { PipesModule } from '@app/shared/pipes/pipes.module';
import { MessageCenterModule } from '@app/modules/message-center/message-center.module';
import { TokenInterceptor } from '@app/core/interceptors/token.interceptor';
import { LanguageInterceptor } from '@app/core/interceptors/language.interceptor';
import { AuthService, LanguageSettingsService } from '@app/core/services';
import { RouteReuseStrategy } from '@angular/router';
import { RouteNoReuseStrategy } from '@app/core/route-reuse-strategies/no-route-reuse.strategy';
import { RootInjector } from '@app/root-injector';
import { Loader } from '@googlemaps/js-api-loader';
import { environment } from '../environments/environment';
import { AppInitializer } from '@app/app-initializer';
import { TranslationLoaderFactory } from '@app/translation-loader-factory';
import { DateAdapter } from '@angular/material/core';
import { AppDateAdapter } from '@app/common/app.date.adapter';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { NgxsModule } from '@ngxs/store';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import duration from 'dayjs/plugin/duration';

dayjs.extend(isoWeek);
dayjs.extend(duration);

WebFont.load({
  custom: { families: ['Material Icons', 'Material Icons Outlined', 'Material Icons Two Tone', 'Material Icons Round', 'Material Icons Sharp'] }
});

@NgModule({
  declarations: [
    AppComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    SharedModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    PipesModule,
    AngularSvgIconModule.forRoot(),
    ToastrModule.forRoot({} as GlobalConfig),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: TranslationLoaderFactory.getTranslateLoader,
        deps: [HttpClient, AuthService, LanguageSettingsService]
      }
    }),
    MessageCenterModule,
    MatProgressBar,
    NgxsModule.forRoot([], { developmentMode: !environment.production })
  ],
  providers: [
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_SELECT_CONFIG, useValue: { overlayPanelClass: 'app-select-overlay' } },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },
    {
      provide: RouteReuseStrategy,
      useClass: RouteNoReuseStrategy
    },
    {
      provide: Loader, // for NgxGpAutocompleteModule
      useValue: new Loader({
        apiKey: environment.googleApiKey,
        libraries: ['places']
      })
    },
    {
      // Angular CDK v21 renders overlays (incl. MatDialog) in the browser top layer
      // via the Popover API by default. That paints above all normal-DOM content
      // regardless of z-index, hiding Google Places' `.pac-container` autocomplete
      // dropdown (appended to document.body) behind dialogs. Opt out to restore the
      // classic z-index-based stacking so the address dropdown is visible again.
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        autoFocus: false,
        enterAnimationDuration: 350,
        exitAnimationDuration: 350,
        panelClass: 'mat-dialog-height-transition'
      } as MatDialogConfig,
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(() => AppInitializer()),
  ]
})
export class AppModule {
  constructor(private injector: Injector) {
    RootInjector.injector = this.injector.get(Injector);
  }
}
